import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";

// Google Sign-In is hidden on iOS builds 30..30 to satisfy guideline 4.8:
// offering a third-party login requires an equivalent privacy login, and build
// 30 shipped without Sign in with Apple. Hidden by DEFAULT on native iOS so the
// button never flashes before the build check resolves (a reviewer can catch
// that flash). Builds below 30 are old TestFlight installs whose passwordless
// Google accounts would otherwise be stranded.
//
// Every Google surface must call this hook — Landing, Login, Register, Account.
// Build 30 was rejected because the hide only covered OAuthButtons (Login /
// Register) and missed the Landing splash + the Account "link Google" option.
const IOS_HIDE_GOOGLE_MIN_BUILD = 30;

// Build 31 is the first binary with the `com.apple.developer.applesignin`
// entitlement AND the CapacitorFirebaseAuthentication/Google subspec. Both
// gates key off it because the web bundle hot-deploys into ALREADY INSTALLED
// apps: shipping either button unconditionally would light it up inside build
// 30, where signInWithApple has no entitlement and signInWithGoogle is
// compiled out by the plugin's default `Lite` subspec. Dead buttons on a live
// app, and a 4.8 violation on the Google one.
const IOS_APPLE_SIGNIN_MIN_BUILD = 31;

function useIosBuild(): { isIosNative: boolean; build: number | null } {
  const isIosNative =
    Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
  const [build, setBuild] = useState<number | null>(null);
  useEffect(() => {
    if (!isIosNative) return;
    let cancelled = false;
    void CapApp.getInfo()
      .then((info) => {
        const parsed = parseInt(String(info.build), 10);
        if (!cancelled && Number.isFinite(parsed)) setBuild(parsed);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isIosNative]);
  return { isIosNative, build };
}

export function useHideGoogleOnIos(): boolean {
  const { isIosNative, build } = useIosBuild();
  if (!isIosNative) return false;
  if (build === null) return true;
  return build >= IOS_HIDE_GOOGLE_MIN_BUILD && build < IOS_APPLE_SIGNIN_MIN_BUILD;
}

// True only inside an iOS binary that actually carries the Sign in with Apple
// entitlement. Apple's own guideline 4.8 does not ask for the button anywhere
// else, so web and Android never show it.
export function useAppleSignInAvailable(): boolean {
  const { isIosNative, build } = useIosBuild();
  return isIosNative && build !== null && build >= IOS_APPLE_SIGNIN_MIN_BUILD;
}
